import { useMemo, useState } from 'react';
import * as THREE from 'three';
import { type ThreeEvent } from '@react-three/fiber';
import { useSketchStore } from '../../state/useSketchStore';
import { solidEffectiveVisible, edgeSelectionId, parseEdgeSelectionId } from '../../core/sketch/types';
import { buildTopology, edgeChainKeys, type SharedEdge } from '../../core/sketch/topology';
import { SKETCH_COLORS } from '../../themes/colors';
import { emitSketchDown, emitSketchMove, type SketchHit } from '../../tools/sketch/sketchToolBus';

/**
 * Sketch solid edge pick layer — the pickable shared borders of solids.
 *
 * Sketch semantics make edges first-class picks (select, move, rotate,
 * erase), but the visual edge lines in SketchSolidLayer are render-only.
 * This layer mirrors the loose-edge approach: each shared edge carries an
 * invisible pick cylinder hugging the border, so the pointer grabs the edge
 * itself while a face pick still wins a few pixels away from the border.
 * Hover and selection highlights render as overlay lines on top.
 */

const PICK_RADIUS = 0.05;
const UP = new THREE.Vector3(0, 1, 0);

function SolidEdgePick({
    solidId,
    edgeKey,
    a,
    b,
    onHover,
}: {
    solidId: string;
    edgeKey: string;
    a: THREE.Vector3;
    b: THREE.Vector3;
    onHover: (edgeKey: string | null) => void;
}) {
    const { mid, quat, length } = useMemo(() => {
        const dir = b.clone().sub(a);
        const len = Math.max(dir.length(), 1e-6);
        return {
            mid: a.clone().add(b).multiplyScalar(0.5),
            quat: new THREE.Quaternion().setFromUnitVectors(UP, dir.normalize()),
            length: len,
        };
    }, [a, b]);

    // The pick cylinder's surface point can sit up to PICK_RADIUS off the
    // border; tools and inference expect the hit ON the edge, so the ray
    // point is clamped to the segment.
    const nearestPointOnEdge = (p: THREE.Vector3): THREE.Vector3 => {
        const ab = b.clone().sub(a);
        const t = THREE.MathUtils.clamp(p.clone().sub(a).dot(ab) / Math.max(ab.lengthSq(), 1e-12), 0, 1);
        return a.clone().addScaledVector(ab, t);
    };

    const hitOf = (e: ThreeEvent<PointerEvent>): SketchHit => ({
        kind: 'solidEdge',
        solidId,
        edgeKey,
        point: nearestPointOnEdge(e.point),
        shiftKey: e.nativeEvent.shiftKey,
        ctrlKey: e.nativeEvent.ctrlKey,
        altKey: e.nativeEvent.altKey,
        button: e.button,
        pointerPx: { x: e.nativeEvent.clientX, y: e.nativeEvent.clientY },
    });

    return (
        <mesh
            position={mid}
            quaternion={quat}
            visible={false}
            onPointerDown={(e: ThreeEvent<PointerEvent>) => {
                if (e.button !== 0) return;
                e.stopPropagation();
                emitSketchDown(hitOf(e));
            }}
            onPointerMove={(e: ThreeEvent<PointerEvent>) => {
                e.stopPropagation();
                emitSketchMove(hitOf(e));
            }}
            onPointerOver={(e: ThreeEvent<PointerEvent>) => {
                e.stopPropagation();
                onHover(edgeKey);
            }}
            onPointerOut={() => onHover(null)}
        >
            <cylinderGeometry args={[PICK_RADIUS, PICK_RADIUS, length, 6]} />
        </mesh>
    );
}

/** One solid's pick proxies + highlight lines, rebuilt only when the solid's
 *  loops change (the topology keys are derived from the contour points). */
function SolidEdgePicks({ solidId }: { solidId: string }) {
    const solid = useSketchStore((s) => s.solids[solidId]);
    const selectedIds = useSketchStore((s) => s.selectedIds);
    const setHovered = useSketchStore((s) => s.setHovered);
    const [hoveredKey, setHoveredKey] = useState<string | null>(null);

    const edges = useMemo(() => {
        if (!solid) return [] as SharedEdge[];
        return Array.from(buildTopology(solid).edges.values());
    }, [solid]);

    const selectedKeys = useMemo(() => {
        const keys = new Set<string>();
        selectedIds.forEach((id) => {
            const parsed = parseEdgeSelectionId(id);
            if (parsed && parsed.solidId === solidId) keys.add(parsed.edgeKey);
        });
        return keys;
    }, [selectedIds, solidId]);

    // Hover highlights the picked edge's whole chain (Sketch's Curve
    // entity read: segments joined through 2-valent corners are one piece).
    const hoverGeometry = useMemo(() => {
        if (!hoveredKey || !solid) return null;
        const chain = new Set(edgeChainKeys(solid, hoveredKey));
        if (chain.size === 0) return null;
        const pts: number[] = [];
        edges.forEach((edge) => {
            if (!chain.has(edge.id)) return;
            pts.push(edge.a.x, edge.a.y, edge.a.z, edge.b.x, edge.b.y, edge.b.z);
        });
        if (pts.length === 0) return null;
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pts), 3));
        return geo;
    }, [hoveredKey, edges, solid]);

    const selectedGeometry = useMemo(() => {
        if (selectedKeys.size === 0) return null;
        const pts: number[] = [];
        edges.forEach((edge) => {
            if (!selectedKeys.has(edge.id)) return;
            pts.push(edge.a.x, edge.a.y, edge.a.z, edge.b.x, edge.b.y, edge.b.z);
        });
        if (pts.length === 0) return null;
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pts), 3));
        return geo;
    }, [selectedKeys, edges]);

    if (edges.length === 0) return null;

    const handleHover = (edgeKey: string | null): void => {
        setHoveredKey(edgeKey);
        setHovered(edgeKey ? edgeSelectionId(solidId, edgeKey) : null);
    };

    return (
        <group>
            {edges.map((edge) => (
                <SolidEdgePick
                    key={edge.id}
                    solidId={solidId}
                    edgeKey={edge.id}
                    a={edge.a}
                    b={edge.b}
                    onHover={handleHover}
                />
            ))}
            {hoverGeometry && (
                <lineSegments geometry={hoverGeometry} raycast={() => null} renderOrder={6}>
                    <lineBasicMaterial color={SKETCH_COLORS.primary} depthTest={false} transparent opacity={0.95} />
                </lineSegments>
            )}
            {selectedGeometry && (
                <lineSegments geometry={selectedGeometry} raycast={() => null} renderOrder={6}>
                    <lineBasicMaterial color={SKETCH_COLORS.faceSelected} depthTest={false} transparent opacity={0.95} />
                </lineSegments>
            )}
        </group>
    );
}

export function SketchSolidEdgePickLayer() {
    const solids = useSketchStore((s) => s.solids);
    const tags = useSketchStore((s) => s.tags);

    // Edge picks stay meaningful in every display style, but hidden or
    // locked solids offer nothing to edit.
    const pickableIds = useMemo(
        () => Object.values(solids)
            .filter((solid) => solidEffectiveVisible(solid, tags) && !solid.locked)
            .map((solid) => solid.id),
        [solids, tags]
    );

    return (
        <group>
            {pickableIds.map((id) => (
                <SolidEdgePicks key={id} solidId={id} />
            ))}
        </group>
    );
}
